import { MealTypeConfig } from '@/components/clients/MealTypesIntegrated';

export interface DCTValidationResult {
  isValid: boolean;
  canProceed: boolean;
  totalMealKcals: number;
  dctTarget: number;
  difference: number;
  percentageDifference: number;
  status: 'within_target' | 'over_target' | 'under_target' | 'no_dct' | 'no_meals';
  message: string;
}

/**
 * Validates meal distribution against Daily Calorie Goals (DCT) with ±10% tolerance
 * Handles two scenarios: all meals have kcal values OR none have kcal values
 */
export const validateMealDistributionAgainstDCT = (
  mealTypes: MealTypeConfig[],
  dctTarget: number | null | undefined
): DCTValidationResult => {
  // Calculate meal kcal statistics
  const mealsWithKcal = mealTypes.filter(meal => meal.targetKcal > 0);
  const mealsWithoutKcal = mealTypes.filter(meal => meal.targetKcal === 0 || !meal.targetKcal);
  const totalMealKcals = mealTypes.reduce((sum, meal) => sum + (meal.targetKcal || 0), 0);

  // Scenario 1: No meal types have kcal values - this is valid, skip DCT comparison
  if (mealsWithKcal.length === 0 && mealsWithoutKcal.length > 0) {
    return {
      isValid: true,
      canProceed: true,
      totalMealKcals: 0,
      dctTarget: dctTarget || 0,
      difference: 0,
      percentageDifference: 0,
      status: 'no_meals',
      message: 'No target calories specified. Edamam-generated values will be used for meal planning.',
    };
  }

  // Handle missing DCT when meals have kcal values
  if (!dctTarget || dctTarget <= 0) {
    return {
      isValid: false,
      canProceed: false,
      totalMealKcals,
      dctTarget: 0,
      difference: 0,
      percentageDifference: 0,
      status: 'no_dct',
      message: 'Daily Calorie Goals not set. Please configure dietary goals first.',
    };
  }

  // Scenario 2: All meal types have kcal values - validate against DCT
  if (mealsWithKcal.length > 0 && mealsWithoutKcal.length === 0) {
    // Continue with existing DCT validation logic
  } else if (mealsWithKcal.length > 0 && mealsWithoutKcal.length > 0) {
    // Mixed scenario - not allowed per the existing validation
    return {
      isValid: false,
      canProceed: false,
      totalMealKcals,
      dctTarget,
      difference: 0,
      percentageDifference: 0,
      status: 'no_meals',
      message: 'All meal types must have consistent target calories (either all filled or all empty).',
    };
  }

  const difference = totalMealKcals - dctTarget;
  const percentageDifference = Math.abs(difference / dctTarget) * 100;
  const isWithinTolerance = percentageDifference <= 10; // ±10% tolerance

  // Determine status and validation result
  if (difference === 0) {
    return {
      isValid: true,
      canProceed: true,
      totalMealKcals,
      dctTarget,
      difference,
      percentageDifference,
      status: 'within_target',
      message: 'Meal distribution matches daily calorie goals exactly.',
    };
  } else if (difference > 0) {
    // Over target
    return {
      isValid: isWithinTolerance,
      canProceed: isWithinTolerance,
      totalMealKcals,
      dctTarget,
      difference,
      percentageDifference,
      status: 'over_target',
      message: `Meal distribution exceeds daily goals by ${Math.abs(difference)} kcal (${percentageDifference.toFixed(1)}%).`,
    };
  } else {
    // Under target
    return {
      isValid: isWithinTolerance,
      canProceed: isWithinTolerance,
      totalMealKcals,
      dctTarget,
      difference,
      percentageDifference,
      status: 'under_target',
      message: `Meal distribution is ${Math.abs(difference)} kcal below daily goals (${percentageDifference.toFixed(1)}%).`,
    };
  }
};

/**
 * Applies DCT caps to meal generation with ±10% tolerance
 * This function ensures that generated meals respect the DCT limits
 */
export const applyDCTCapsToMealGeneration = (
  proposedMealKcals: number[],
  dctTarget: number,
  allowedVariancePercent: number = 10
): {
  adjustedMealKcals: number[];
  totalAdjusted: number;
  wasAdjusted: boolean;
  adjustmentReason?: string;
} => {
  const totalProposed = proposedMealKcals.reduce((sum, kcal) => sum + kcal, 0);
  const maxAllowed = dctTarget * (1 + allowedVariancePercent / 100);
  const minAllowed = dctTarget * (1 - allowedVariancePercent / 100);

  // If within range, return as-is
  if (totalProposed >= minAllowed && totalProposed <= maxAllowed) {
    return {
      adjustedMealKcals: [...proposedMealKcals],
      totalAdjusted: totalProposed,
      wasAdjusted: false,
    };
  }

  // Calculate adjustment factor
  let targetTotal: number;
  let adjustmentReason: string;

  if (totalProposed > maxAllowed) {
    targetTotal = maxAllowed;
    adjustmentReason = `Total exceeded DCT by more than ${allowedVariancePercent}%. Capped at ${maxAllowed} kcal.`;
  } else {
    targetTotal = minAllowed;
    adjustmentReason = `Total was below DCT by more than ${allowedVariancePercent}%. Raised to ${minAllowed} kcal.`;
  }

  const adjustmentFactor = targetTotal / totalProposed;
  
  // Apply proportional adjustment to each meal
  const adjustedMealKcals = proposedMealKcals.map(kcal => {
    const adjusted = Math.round(kcal * adjustmentFactor);
    // Ensure individual meals also respect ±10% of their original values
    const mealMin = Math.max(1, Math.round(kcal * 0.9));
    const mealMax = Math.round(kcal * 1.1);
    return Math.min(mealMax, Math.max(mealMin, adjusted));
  });

  // Fine-tune to match target exactly
  const adjustedTotal = adjustedMealKcals.reduce((sum, kcal) => sum + kcal, 0);
  const finalDifference = targetTotal - adjustedTotal;

  if (Math.abs(finalDifference) > 0) {
    // Distribute the difference across meals proportionally
    const largestMealIndex = adjustedMealKcals.indexOf(Math.max(...adjustedMealKcals));
    adjustedMealKcals[largestMealIndex] += finalDifference;
  }

  return {
    adjustedMealKcals,
    totalAdjusted: adjustedMealKcals.reduce((sum, kcal) => sum + kcal, 0),
    wasAdjusted: true,
    adjustmentReason,
  };
};

/**
 * Validates individual meal kcal values against ±10% of their target
 */
export const validateIndividualMealKcals = (
  actualKcals: number[],
  targetKcals: number[],
  allowedVariancePercent: number = 10
): {
  isValid: boolean;
  violations: Array<{
    mealIndex: number;
    actual: number;
    target: number;
    difference: number;
    percentageDifference: number;
  }>;
} => {
  const violations: Array<{
    mealIndex: number;
    actual: number;
    target: number;
    difference: number;
    percentageDifference: number;
  }> = [];

  actualKcals.forEach((actual, index) => {
    const target = targetKcals[index] || 0;
    if (target > 0) {
      const difference = Math.abs(actual - target);
      const percentageDifference = (difference / target) * 100;
      
      if (percentageDifference > allowedVariancePercent) {
        violations.push({
          mealIndex: index,
          actual,
          target,
          difference,
          percentageDifference,
        });
      }
    }
  });

  return {
    isValid: violations.length === 0,
    violations,
  };
};
